import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Link,
  Typography,
} from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext, SubscriptionTab } from "contexts/Subscription";
import { useContext } from "react";
import { SubscriptionType } from "schemas/dashboard";
import { Plans } from "schemas/payments";

import { PRO_TERMS_AND_CONDITIONS_LINK } from "constants/influencer";
import { eligibleForProDiscount } from "features/Home/Banner/helpers";
import { trackEvent } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

import PlansPrice from "../SharedComponents/Price";
import {
  PlanAction,
  getCurrentPlanButton,
  getPlanLimitText,
  isCurrentPlan,
} from "../helpers";
import FollowUpAssistantItem from "./FollowUpAssistantItem";
import styles from "./styles";

interface Props {
  plan: Plans;
}

const ProPlanCard = ({ plan }: Props) => {
  const { currentOrg, organizationProProfile } = useContext(
    OrganizationUserContext,
  );
  const {
    plans,
    emailSents,
    subscription,
    tab,
    bannerPromoCodes,
    isAtEmailCreditCap,
  } = useContext(SubscriptionContext);
  const { onPlanButtonClick } = useSubscription();

  const planName = plans?.[plan]?.display_name;
  const totalEmails = plans?.[plan]?.monthly_limit || 0;
  const action = getCurrentPlanButton(
    plan,
    subscription?.subscription?.planName || Plans.FREE,
    subscription,
  );

  const trackTermsAndCondition = () => {
    trackEvent("Terms and Conditions for Pro Plan Clicked");
  };

  const BENEFITS = [
    {
      icon: "🚀",
      primary: (
        <>
          Send <strong>{totalEmails} emails</strong> per month
        </>
      ),
    },
    {
      icon: "🧠",
      primary: (
        <>
          Get <strong>Advanced Brand Filters</strong> (e.g "Brands that work
          with UGC creators") to refine your search
        </>
      ),
    },
    {
      icon: "📈",
      primary: (
        <>
          <strong> See reply likelihood scores</strong> for each contact
        </>
      ),
    },
    {
      icon: "🔑",
      primary: (
        <>
          Get access to <strong>600k+ Brand Contacts</strong>
        </>
      ),
    },
  ];

  const _isCurrentPlan =
    isCurrentPlan(subscription, plan) &&
    (subscription?.subscription?.status === SubscriptionType.ACTIVE ||
      subscription?.subscription?.status === SubscriptionType.PAST_DUE);

  return (
    <>
      <Box sx={styles.card}>
        <Box sx={[styles.planTitleCard, { backgroundColor: "primary.main" }]}>
          <Grid container justifyContent="flex-end">
            <Chip
              sx={{ backgroundColor: "yellow.main", px: 1 }}
              label={
                <Typography variant="subtitle2" sx={{ fontSize: 13 }}>
                  <strong>Great for creators who pitch often</strong>
                </Typography>
              }
            ></Chip>
          </Grid>

          <Typography variant="h3" sx={{ mt: 6, color: "white" }}>
            {planName}{" "}
            {_isCurrentPlan && (
              <Box component="span" sx={{ color: "yellow.main", fontSize: 16 }}>
                (Current Plan)
              </Box>
            )}
          </Typography>

          <Box sx={styles.price}>
            {tab === SubscriptionTab.monthly && <PlansPrice plan={Plans.PRO} />}
            {tab === SubscriptionTab.yearly && (
              <PlansPrice plan={Plans.PRO_YEARLY} />
            )}
          </Box>
          <Grid item xs={12}>
            <Link
              href={PRO_TERMS_AND_CONDITIONS_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackTermsAndCondition}
              sx={{
                color: "yellow.main",
                fontSize: 10,
                textDecoration: "underline",
                textDecorationColor: "yellow.main",
              }}
            >
              Terms & Conditions
            </Link>
          </Grid>
          {_isCurrentPlan && !isAtEmailCreditCap && (
            <Box sx={styles.rolloverBox}>
              <Box sx={{ ...styles.rolloverRow, mb: 0.5 }}>
                <Typography sx={styles.rolloverLabelDim}>
                  Email Credits Used
                </Typography>
                <Typography sx={styles.rolloverValue}>
                  {emailSents} /{" "}
                  {Math.max(emailSents, subscription?.monthlyLimit || 0)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(
                  100,
                  (emailSents /
                    Math.max(1, subscription?.monthlyLimit || emailSents)) *
                    100,
                )}
                sx={styles.rolloverProgress}
              />
              <Box sx={{ ...styles.rolloverRow, mb: 0.25 }}>
                <Typography sx={styles.rolloverLabel}>
                  Monthly Allotment
                </Typography>
                <Typography sx={styles.rolloverLabel}>
                  {(
                    (subscription?.monthlyLimit || 0) -
                    (subscription?.rolloverEmailCredits || 0)
                  ).toLocaleString()}
                </Typography>
              </Box>
              {(subscription?.rolloverEmailCredits || 0) > 0 && (
                <Box sx={styles.rolloverRow}>
                  <Typography sx={styles.rolloverLabel}>
                    Rolled over from last month
                  </Typography>
                  <Typography sx={styles.rolloverValueAccent}>
                    +{subscription!.rolloverEmailCredits!.toLocaleString()}
                  </Typography>
                </Box>
              )}
              <Divider sx={styles.rolloverDivider} />
              <Box sx={styles.rolloverRow}>
                <Typography sx={styles.rolloverTotal}>
                  Total available this month
                </Typography>
                <Typography sx={styles.rolloverTotal}>
                  {Math.max(
                    emailSents,
                    subscription?.monthlyLimit || 0,
                  ).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {!isAtEmailCreditCap && (
          <Box
            sx={{
              backgroundColor: "#fef9ec",
              border: "1px solid #fde68a",
              borderRadius: 2,
              px: 2,
              py: 1.5,
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                mb: 0.25,
              }}
            >
              <Box
                component="i"
                className="fa-solid fa-arrow-trend-up"
                sx={{ fontSize: 13, color: "#d97706" }}
              />
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                {_isCurrentPlan ? "Credit Rollover Active" : "Rollover Credits"}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              {_isCurrentPlan
                ? "Unused credits roll over each month (up to 250/month). You can build up a maximum of 1,000 total email credits per month."
                : "Unused emails roll over monthly"}
            </Typography>
          </Box>
        )}

        {BENEFITS.map((benefit, index) => (
          <Grid container key={index} alignItems="center" sx={{ mb: 1.5 }}>
            <Grid
              container
              justifyContent="left"
              alignItems="center"
              item
              xs="auto"
            >
              <Box sx={styles.iconBlock}>{benefit.icon}</Box>
            </Grid>
            <Grid item xs md>
              <Typography component="div">{benefit.primary}</Typography>
            </Grid>
          </Grid>
        ))}
        <FollowUpAssistantItem
          isCurrentPlan={_isCurrentPlan}
          subscription={subscription}
        />

        {_isCurrentPlan &&
          organizationProProfile?.planConfig?.monthlyLimit &&
          organizationProProfile.planConfig.monthlyLimit !== totalEmails && (
            <Alert severity="info">
              <Typography
                sx={{
                  fontSize: 12,
                  color: "black",

                  fontWeight: 500,
                }}
              >
                As an existing subscriber, your Pro plan includes{" "}
                <strong>
                  {organizationProProfile.planConfig.monthlyLimit} emails/month
                </strong>{" "}
              </Typography>
            </Alert>
          )}

        {!currentOrg?.overridePlanName && (
          <Button
            disableElevation
            fullWidth
            variant={action === PlanAction.upgrade ? "contained" : "text"}
            sx={{
              mt: 3,
              py: 1,
              borderRadius: 2,
              backgroundColor:
                action === PlanAction.upgrade ? "primary.main" : "#f4f8f6",
            }}
            onClick={() => onPlanButtonClick(plan, action)}
          >
            {action}{" "}
            {tab === SubscriptionTab.yearly &&
            (action === PlanAction.upgrade || action === PlanAction.downgrade)
              ? "+ Get Free Template"
              : ""}
          </Button>
        )}
      </Box>

      <Box sx={{ mt: 2 }}>
        {getPlanLimitText(
          subscription,
          plan,
          emailSents,
          plans,
          bannerPromoCodes,
          eligibleForProDiscount(currentOrg, plans, subscription),
        )}
      </Box>
    </>
  );
};

export default ProPlanCard;
